"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildJEVMImportTransaction = exports.buildJEVMExportTransaction = exports.buildTransactionEVMOutputs = exports.buildTransactionEVMInputs = void 0;
const utils_1 = require("../../utils");
const builder_1 = require("../builder");
const input_1 = require("../input");
const transaction_1 = require("../transaction");
const types_1 = require("../types");
const transaction_2 = require("./transaction");
function buildTransactionEVMInputs(userInputs, signer, nonce, fees) {
    const inputs = [];
    const values = new Map();
    // merging inputs
    for (const userInput of userInputs) {
        const assetId = userInput.assetId;
        let value = userInput.amount;
        if (values.has(assetId)) {
            value += values.get(assetId);
        }
        values.set(assetId, value);
    }
    // adding and merging fees
    for (const fee of fees) {
        if (fee.amount > 0) {
            const assetId = fee.assetId;
            let value = fee.amount;
            if (values.has(assetId)) {
                value += values.get(assetId);
            }
            values.set(assetId, value);
        }
    }
    // adding inputs
    for (const [key, value] of values) {
        inputs.push(new transaction_2.EVMInput(new types_1.Address(signer), value, new types_1.AssetId(key), nonce));
    }
    return inputs;
}
exports.buildTransactionEVMInputs = buildTransactionEVMInputs;
function buildTransactionEVMOutputs(userInputs, inputs, feeData) {
    const spentAmounts = new Map();
    // add fees as already spent so they are not added in outputs
    spentAmounts.set(feeData.assetId, feeData.amount);
    // adding outputs matching user inputs
    const outputs = [];
    for (const input of userInputs) {
        const assetId = input.assetId;
        if (input.addresses.length !== 1) {
            throw new utils_1.InputError('user input must have a unique address for EVM output');
        }
        outputs.push(new transaction_2.EVMOutput(new types_1.Address(input.addresses[0]), input.amount, new types_1.AssetId(assetId)));
        let spentAmount = input.amount;
        if (spentAmounts.has(assetId)) {
            spentAmount += spentAmounts.get(assetId);
        }
        spentAmounts.set(assetId, spentAmount);
    }
    const availableAmounts = {};
    // getting the total amount spendable for each asset in provided inputs
    for (const input of inputs) {
        const availableAmount = availableAmounts[input.getAssetId().value];
        const amount = input.getAmount();
        if (availableAmount === undefined) {
            availableAmounts[input.getAssetId().value] = BigInt(amount);
        }
        else {
            availableAmounts[input.getAssetId().value] += BigInt(amount);
        }
    }
    // verifying that inputs have the funds to pay for the spent amounts
    for (const input of inputs) {
        const assetId = input.getAssetId().value;
        const spent = spentAmounts.has(assetId) ? spentAmounts.get(assetId) : BigInt(0);
        const available = availableAmounts[assetId];
        if (spent > available) {
            throw new utils_1.OutputError('output would produce more than provided inputs');
        }
    }
    return outputs;
}
exports.buildTransactionEVMOutputs = buildTransactionEVMOutputs;
function buildJEVMExportTransaction(userInputs, signer, nonce, exportAddress, sourceFee, destinationFee, networkId) {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jevm export transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jevm export transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const sourceFeeData = new transaction_1.TransactionFee(userInputs[0].sourceChain, sourceFee);
    const destinationFeeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, destinationFee);
    const fees = [sourceFeeData, destinationFeeData];
    const inputs = buildTransactionEVMInputs(userInputs, signer, nonce, fees);
    // fixed user inputs with a defined export address to import it later
    const fixedUserInputs = [];
    for (const input of userInputs) {
        fixedUserInputs.push(new input_1.UserInput(input.assetId, input.sourceChain, input.amount, [exportAddress], 1, input.destinationChain));
    }
    if (destinationFeeData.amount > BigInt(0)) {
        // adding fees as user input to be able to export it
        fixedUserInputs.push(new input_1.UserInput(destinationFeeData.assetId, userInputs[0].sourceChain, destinationFeeData.amount, [exportAddress], 1, userInputs[0].destinationChain));
    }
    // we provide a change address but this will not be used here because
    // inputs will have fixed value as they are EVM Inputs
    const exportedOutputs = (0, builder_1.buildTransactionOutputs)(fixedUserInputs, inputs, sourceFeeData, signer);
    return new transaction_2.JEVMExportTransaction(networkId, new types_1.BlockchainId(sourceId), new types_1.BlockchainId(destinationId), inputs, exportedOutputs);
}
exports.buildJEVMExportTransaction = buildJEVMExportTransaction;
function buildJEVMImportTransaction(userInputs, utxoSet, signersAddresses, fee, networkId) {
    if (userInputs.length < 1) {
        throw new utils_1.InputError('user inputs cannot be empty');
    }
    const sourceId = userInputs[0].sourceChain.id;
    const destinationId = userInputs[0].destinationChain.id;
    for (const input of userInputs) {
        if (input.sourceChain.id !== sourceId || input.destinationChain.id !== destinationId) {
            throw new utils_1.InputError('jvm import transaction cannot have different source or destination chain user inputs');
        }
        if (input.sourceChain.id === input.destinationChain.id) {
            throw new utils_1.InputError('jvm import transaction cannot have the same chain as source and destination user inputs');
        }
    }
    const feeData = new transaction_1.TransactionFee(userInputs[0].destinationChain, fee);
    const importedInputs = (0, builder_1.buildTransactionInputs)(userInputs, utxoSet, signersAddresses, [feeData]);
    const outputs = buildTransactionEVMOutputs(userInputs, importedInputs, feeData);
    return new transaction_2.JEVMImportTransaction(networkId, new types_1.BlockchainId(destinationId), new types_1.BlockchainId(sourceId), importedInputs, outputs);
}
exports.buildJEVMImportTransaction = buildJEVMImportTransaction;
